import MojitoCore from 'mojito/core';
import MojitoServices from 'mojito/services';
import MojitoModules from 'mojito/modules';

import {resolveBackground} from '#core/utils/color-utils.js';
import {icon} from '#core/utils/style-utils.js';
import {isMobile} from '#core/utils/context-utils.js';

import {generatePivotsRow} from '#core/application/modules/tiles/pivot/index.js';
import {generateSkeletonPart} from '#core/application/modules/tiles/skeleton-part/index.js';
import {generatePromotionQuickLinks} from '#core/application/modules/tiles/promotion-quick-links/index.js';
import {generateBorder} from '#core/application/modules/tiles/border/index.js';

import {getSportBackgroundImages} from './utils.js';

const {types: SportGroupsListTypes} = MojitoModules.SportGroupsList;
const {LOCATION} = MojitoServices.Promotions.types;
const {merge} = MojitoCore.Presentation.uiStyle;

export const SPORT_BACKGROUND_IMAGE_MAP = {
    american_football: 'american_football',
    baseball: 'baseball',
    basketball: 'basketball',
    boxing: 'boxing',
    cricket: 'cricket',
    cycling: 'cycling',
    darts: 'darts',
    football: 'soccer',
    futsal: 'futsal',
    golf: 'golf',
    handball: 'handball',
    ice_hockey: 'ice_hockey',
    martial_arts: 'martial_arts',
    motorsport: 'motorsport',
    snooker: 'snooker',
    soccer: 'soccer',
    table_tennis: 'table_tennis',
    tennis: 'tennis',
    volleyball: 'volleyball',
    horse_racing: 'horse_racing',
    greyhound_racing: 'greyhound_racing',
};

// DBX-12727
const SPORT_COUPON_TYPE_CLASS_SKELETON_PART = !isMobile()
    ? {
          style: {
              flexDirection: 'row',
              alignItems: 'center',
          },
      }
    : {};

export function sportsPagesFactory(PALETTE) {
    return {
        SportPage: {
            leftPromotions: {
                locationId: LOCATION.LEFT,
            },
            sportBackgroundImages: getSportBackgroundImages({sportBackgroundImageMap: SPORT_BACKGROUND_IMAGE_MAP}),
            _mobile_sportBackgroundImages: getSportBackgroundImages({
                sportBackgroundImageMap: SPORT_BACKGROUND_IMAGE_MAP,
                folderName: 'mobile',
            }),
            pageHeader: {
                headerContainer: {
                    style: {
                        borderTop: 'none',
                        ...resolveBackground(PALETTE.SPORT_HEADER_BG),
                    },
                },
                label: {
                    style: {
                        zIndex: 1,
                        ...PALETTE.SPORT_HEADER_TEXT,
                    },
                },
                icon: {
                    style: {
                        fill: PALETTE.SPORT_HEADER_ICON_COLOR,
                    },
                },
                backButton: {
                    buttonIcon: {
                        style: {
                            fill: PALETTE.SPORT_HEADER_BACK_BUTTON_ICON_COLOR,
                        },
                    },
                },
                backgroundImage: {
                    src: 'sport-page/generic.png',
                    _mobile_src: 'sport-page/mobile/generic.png',
                    style: {
                        fill: PALETTE.SPORT_HEADER_IMAGE_COLOR,
                        opacity: PALETTE.SPORT_HEADER_IMAGE_OPACITY,
                        objectFit: 'fill',
                        width: 'auto',
                        height: '100%',
                        justifyContent: 'end',
                    },
                },
                backgroundImageContainer: {
                    style: {
                        width: 'auto',
                    },
                },
            },
            sportTabSelector: {
                tabs: merge(generatePivotsRow(PALETTE.SPORT_TABS_PIVOT), {
                    style: {
                        container: {
                            backgroundColor: 'transparent',
                            boxShadow: 'none',
                            margin: 0,
                            padding: '0 4px',
                        },
                    },
                }),
            },
        },
        CompetitionsView: {
            container: {
                style: {
                    backgroundColor: 'transparent',
                },
            },
            menuLinkPromotions: merge(generatePromotionQuickLinks(PALETTE.COMPETITIONS_PROMOTION_QUICK_LINKS), {
                menuRow: {
                    buttonScrollPane: {
                        scrollPane: {
                            style: {
                                padding: '0 8px',
                            },
                        },
                    },
                    itemInnerContainer: {
                        style: {
                            padding: '0 8px',
                        },
                    },
                },
            }),
        },
        OutrightsView: {
            container: {
                style: {
                    backgroundColor: 'transparent',
                },
            },
        },
        EventListHeader: {
            marketOptionsContainer: {
                container: {
                    style: {
                        display: 'flex', // fix for Mojito wrong behaviour
                        _mobile_display: 'block', // moved from Mojito default
                        _desktop_justifyContent: 'flex-end',
                        flexGrow: 0,
                        flexShrink: 0,
                        flexBasis: 'auto',
                    },
                },
                sportWidthOverride: null,
            },
            marketSwitcher: {
                menuContainer: {
                    style: {
                        maxWidth: '220px',
                        _mobile_maxWidth: '184px',
                        width: '100%',
                    },
                },
            },
            title: {
                truncate: true,
            },
        },
        ...generateLeagues(PALETTE),
        ...generateEventListSkeleton(PALETTE),
    };
}

function generateLeagues(PALETTE) {
    const LEAGUES_LIST_CONTAINER = {
        style: {
            ...resolveBackground(PALETTE.LEAGUES_CONTAINER_BG),
            padding: 0,
            overflow: 'hidden',
            ...generateBorder(PALETTE.LEAGUES_CONTAINER_BORDER),
        },
    };

    return {
        SportGroupsListView: {
            defaultSortingMethod: GENBET_BASED
                ? SportGroupsListTypes.SORTING_TYPES.BY_DISPLAY_ORDER
                : SportGroupsListTypes.SORTING_TYPES.BY_NAME,
            sortEventsToggleButton: {
                buttonIcon: icon(PALETTE.LEAGUES_LIST_HEADER_SORT_ICON_COLOR),
                buttonLabel: {
                    style: PALETTE.LEAGUES_LIST_HEADER_SORT_TEXT,
                },
            },
            headerText: {
                style: PALETTE.LEAGUES_SECTION_HEADER_TEXT,
            },
            listContainer: LEAGUES_LIST_CONTAINER,
            locationContainer: {
                style: {
                    minHeight: '40px',
                    padding: '0 8px',
                    ...resolveBackground(PALETTE.LEAGUES_HEADER_BG),
                },
            },
            locationName: {
                style: {
                    ...PALETTE.LEAGUES_HEADER_TEXT,
                },
            },
            sportGroupButton: {
                style: {
                    base: {
                        minHeight: '40px',
                        padding: '0 8px',
                    },
                },
            },
            sportGroupName: {
                style: {
                    ...PALETTE.LEAGUES_ITEM_NAME_TEXT,
                },
            },
            chevronIcon: icon(PALETTE.LEAGUES_ITEM_CHEVRON_ICON_COLOR),
            countryOrSportIcon: {
                sportIcon: {
                    icon: icon(PALETTE.LEAGUES_ITEM_SPORT_ICON_COLOR),
                },
            },
        },
        SportGroupsListSkeleton: {
            headerContainer: {
                style: {
                    padding: '0 8px',
                },
            },
            headerText: {
                style: PALETTE.LEAGUES_SECTION_HEADER_TEXT,
            },
            iconPlaceholder: {
                style: {
                    height: '16px',
                },
            },
            regionPlaceholder: {
                style: {
                    height: '16px',
                },
            },
            leaguePlaceholder: {
                style: {
                    height: '16px',
                },
            },
            listContainer: merge(LEAGUES_LIST_CONTAINER, {
                itemSpacing: '24px',
                style: {
                    padding: 8,
                },
            }),
            sportGroupsGrid: {
                style: {
                    gap: 8,
                },
            },
        },
    };
}

function generateEventListSkeleton(PALETTE) {
    const {EVENT_LIST_SKELETON} = PALETTE;

    return {
        EventListSkeleton: {
            container: {
                style: {
                    ...resolveBackground(EVENT_LIST_SKELETON.EVENT_LIST_BG),
                    padding: 0,
                },
                separator: {
                    style: {
                        size: EVENT_LIST_SKELETON.EVENT_LIST_HEADER.EVENT_LIST_HEADER_WITH_SEPARATOR ? '1px' : '0px',
                        color: EVENT_LIST_SKELETON.EVENT_LIST_HEADER.EVENT_LIST_HEADER_SEPARATOR_COLOR,
                    },
                },
            },
            outrightsSkeletonContainer: {
                style: {
                    ...resolveBackground(EVENT_LIST_SKELETON.EVENT_LIST_BG),
                    padding: '0 8px',
                },
                separator: {
                    style: {
                        size: EVENT_LIST_SKELETON.EVENT_LIST_HEADER.EVENT_LIST_HEADER_WITH_SEPARATOR ? '1px' : '0px',
                        color: EVENT_LIST_SKELETON.EVENT_LIST_HEADER.EVENT_LIST_HEADER_SEPARATOR_COLOR,
                    },
                },
            },
            line: {
                style: {
                    height: 50,
                },
            },
            eventListHeaderContainer: {
                style: {
                    ...resolveBackground(EVENT_LIST_SKELETON.EVENT_LIST_HEADER.EVENT_LIST_HEADER_BG),
                    padding: '0 8px',
                },
            },
            sortingSelector: generateSkeletonPart(PALETTE.EVENT_LIST_HEADER_ITEM_SKELETON_PART),
            marketSwitcherWhenSortingEnabled: generateSkeletonPart(PALETTE.EVENT_LIST_HEADER_ITEM_SKELETON_PART),
            groupHeaderContainer: {
                style: {
                    ...resolveBackground(EVENT_LIST_SKELETON.EVENT_LIST_GROUP_HEADER_BG),
                    height: '42px',
                    padding: '0 8px',
                },
            },
            typeAndClassContainer: SPORT_COUPON_TYPE_CLASS_SKELETON_PART,
            type: generateSkeletonPart(PALETTE.EVENT_LIST_GROUP_HEADER_ITEM_SKELETON_PART),
            class: generateSkeletonPart(PALETTE.EVENT_LIST_GROUP_HEADER_ITEM_SKELETON_PART),
            selectionHeader: generateSkeletonPart(PALETTE.EVENT_LIST_GROUP_HEADER_ITEM_SKELETON_PART),
            eventsContainer: {
                style: {
                    padding: '8px',
                },
                separator: {
                    style: {
                        color: EVENT_LIST_SKELETON.EVENT_LIST_GROUP_EVENTS_SEPARATOR_COLOR,
                    },
                },
            },
        },
        EventListItemSkeleton: {
            participant: merge(generateSkeletonPart(PALETTE.EVENT_LIST_ITEM_SKELETON_PART), {
                style: {
                    _mobile_width: '85px',
                },
            }),
            participantsAndMarketsContainer: {
                _mobile_itemSpacing: '4px',
            },
            americanParticipantContainer: {
                _mobile_itemSpacing: '4px',
            },
        },
    };
}
